import { useEffect, useMemo, useRef, useState } from 'react';
import databaseMonitorService, { ProcessRow } from '../../../services/databaseMonitor.service';
import { ProcessListTable } from './components/ProcessListTable';
import { KillConfirmDialog } from './components/KillConfirmDialog';
import { Button } from './components/ui';
import { useToast } from '../../../contexts/ToastContext';

const INTERVALS = [2000, 3000, 5000, 10000];

interface Props {
  serverId: string;
}

export default function ProcessListTab({ serverId }: Props) {
  const { showToast } = useToast();
  const [pollMs, setPollMs] = useState(3000);
  const [hideSleep, setHideSleep] = useState(true);
  const [killTarget, setKillTarget] = useState<ProcessRow | null>(null);
  const [rows, setRows] = useState<ProcessRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [dataUpdatedAt, setDataUpdatedAt] = useState<number>(0);
  const [killing, setKilling] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useMemo(
    () => async (signal: AbortSignal) => {
      setIsFetching(true);
      try {
        const data = await databaseMonitorService.getProcesslist(serverId, signal);
        if (signal.aborted) return;
        setRows(data);
        setError(null);
        setDataUpdatedAt(Date.now());
      } catch (err: any) {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || signal.aborted) return;
        setError(err?.response?.data?.detail ?? err?.message ?? 'Failed to load processlist');
      } finally {
        if (!signal.aborted) setIsFetching(false);
      }
    },
    [serverId]
  );

  useEffect(() => {
    abortRef.current?.abort();
    if (timerRef.current) clearTimeout(timerRef.current);

    let cancelled = false;
    const controller = new AbortController();
    abortRef.current = controller;

    const run = async () => {
      if (!document.hidden) await load(controller.signal);
      if (cancelled || controller.signal.aborted) return;
      timerRef.current = setTimeout(run, pollMs);
    };

    run();

    return () => {
      cancelled = true;
      controller.abort();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [load, pollMs, refreshTick]);

  const visibleRows = useMemo(
    () => (hideSleep ? rows.filter((r) => r.command !== 'Sleep') : rows),
    [rows, hideSleep]
  );
  const hiddenCount = rows.length - visibleRows.length;

  const handleKill = async () => {
    if (!killTarget) return;
    setKilling(true);
    try {
      await databaseMonitorService.killProcess(serverId, killTarget.id);
      showToast({ type: 'success', message: `Killed query ${killTarget.id}` });
      setKillTarget(null);
      setRefreshTick((t) => t + 1);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        showToast({ type: 'info', message: 'Query already finished' });
        setKillTarget(null);
        setRefreshTick((t) => t + 1);
      } else {
        showToast({
          type: 'error',
          message: err?.response?.data?.detail ?? err?.message ?? 'Failed to kill query',
        });
      }
    } finally {
      setKilling(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col gap-4">
      <div className="flex items-center justify-end gap-3 text-sm flex-wrap">
        <label className="text-slate-600 dark:text-slate-400 flex items-center gap-2 select-none cursor-pointer">
          <input
            type="checkbox"
            checked={hideSleep}
            onChange={(e) => setHideSleep(e.target.checked)}
            className="accent-sky-500"
          />
          Hide Sleep
        </label>
        <label className="text-slate-600 dark:text-slate-400 flex items-center gap-2">
          Refresh
          <select
            className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-md px-2 py-1 text-sm text-slate-900 dark:text-slate-100"
            value={pollMs}
            onChange={(e) => setPollMs(Number(e.target.value))}
          >
            {INTERVALS.map((ms) => (
              <option key={ms} value={ms}>
                {ms / 1000}s
              </option>
            ))}
          </select>
        </label>
        <Button variant="secondary" onClick={() => setRefreshTick((t) => t + 1)}>
          Refresh now
        </Button>
      </div>

      {error ? (
        <div className="text-red-600 dark:text-red-400">Error: {error}</div>
      ) : (
        <ProcessListTable rows={visibleRows} onKill={setKillTarget} />
      )}

      <div className="text-xs text-slate-500 dark:text-slate-500 flex gap-3">
        <span>
          {isFetching ? 'Refreshing…' : dataUpdatedAt ? `Updated ${relative(dataUpdatedAt)}` : ''}
        </span>
        {hideSleep && hiddenCount > 0 && (
          <span>
            {hiddenCount} Sleep row{hiddenCount === 1 ? '' : 's'} hidden
          </span>
        )}
      </div>

      <KillConfirmDialog
        row={killTarget}
        submitting={killing}
        onClose={() => (!killing ? setKillTarget(null) : undefined)}
        onConfirm={handleKill}
      />
    </div>
  );
}

function relative(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 1000) return 'just now';
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  return `${Math.floor(diff / 60_000)}m ago`;
}
